import type { TrackTheme } from '@/world/Theme';
import type { TrackConfig } from '@/world/Track';
import type { DifficultyLevel } from '@/config/tuning';
import { tuning } from '@/config/tuning';

/**
 * cities.ts — the nine Tour cities, in order. THIS is the creative-direction file:
 * each city is a palette (sky / fog / light / surface tints — varied time-of-day),
 * a per-city track shape (curves get sharper as the Tour goes on), an AI
 * difficulty, a fixed seed (so a city always plays the same), and real lat/lon for
 * the M9 globe. Tweak palettes freely — colours are hex (three.js style).
 *
 * Difficulty + curve sharpness RAMP across the nine: NYC is the gentle opener,
 * Patagonia the sharp, twisty finale. Landmarks + 3D scenery are layered on per
 * city in the scenery pass (bold low-poly, swappable for the Design pass).
 */
/**
 * The kinds of roadside scenery a stretch of track can have. A city's `route` is
 * a list of zones placed by progress to the finish, so the scenery CHANGES as you
 * drive (e.g. NYC: Harlem brownstones → Central Park → Broadway towers → Times Sq).
 */
export type SceneryKind =
  | 'brownstones'
  | 'park'
  | 'skyscrapers'
  | 'timessquare'
  | 'westminster' // London: cream Portland-stone civic blocks
  | 'thames' // London: the open river on one side, embankment on the other
  | 'spaccanapoli' // Napoli: tight pastel old-town alleys, laundry overhead
  | 'bayfront' // Napoli: the bay on one side, palms + pastel hotels on the other
  | 'castello' // Napoli: heavy tufa-stone castle quarter
  | 'piazza' // Napoli: open cream plaza for the grand finale
  | 'shibuya' // Tokyo: dark ad-towers, glyph sign columns, electric colour
  | 'asakusa' // Tokyo: low timber temple town, lanterns + cherry blossom
  | 'minato' // Tokyo: night office towers around Tokyo Tower
  | 'bayfinish' // Tokyo: the ink-dark bay + wharf sheds into the finish
  | 'copacabana' // Rio: wave promenade, sand + turquoise sea, white hotels
  | 'lapa' // Rio: bright colonial houses under the aqueduct
  | 'tijuca' // Rio: lush rainforest + jungle hills
  | 'maracana' // Rio: palm boulevard to the stadium
  | 'medina' // Dakar: flat-roofed render blocks, bright doors, sandy verges
  | 'market' // Dakar: stall + parasol bustle
  | 'savanna' // Dakar: open sand, baobabs, dunes — rally country
  | 'lacrose' // Dakar: the PINK lake, salt mounds + pirogues
  | 'balogun' // Lagos: loud commercial blocks, market umbrellas, danfos
  | 'lagoon' // Lagos: the causeway — water BOTH sides, stilt village, canoes
  | 'victoriaIsland' // Lagos: modern glass towers with colour pops
  | 'lekki' // Lagos: the approach to the cable-stayed bridge
  | 'fort' // Mumbai: Victorian-Gothic civic quarter, red buses, kaali-peelis
  | 'marinedrive' // Mumbai: the Queen's Necklace sweep — sea wall + art deco
  | 'hajiali' // Mumbai: the causeway water with the dargah offshore
  | 'colaba' // Mumbai: grand hotel quarter to the Gateway
  | 'elchalten' // Patagonia: frontier town — bright-roofed cabins, fences
  | 'lenga' // Patagonia: crimson southern-beech forest, moraine hills
  | 'glaciallake' // Patagonia: the milky turquoise lake, icebergs adrift
  | 'glacier' // Patagonia: snowfields beneath the great ice wall
  | 'generic';

/** One stretch of the route: this scenery applies up to `until` (0..1 progress). */
export interface RouteZone {
  until: number;
  kind: SceneryKind;
  /** Optional human label (purely for readability / future signage). */
  label?: string;
}

/** A unique hero landmark model (built once, not instanced). */
export type LandmarkKind =
  // New York
  | 'apollo'
  | 'hamiltonGrange'
  | 'langstonHughes'
  | 'belvedereCastle'
  | 'bowBridge'
  | 'bethesdaFountain'
  | 'empireState' // the limestone setbacks + lit crown + mast (Broadway stretch)
  | 'chrysler' // the stainless sunburst crown + needle
  | 'nyeBall'
  // London
  | 'bigBen' // Elizabeth Tower + the Houses of Parliament (one hero group)
  | 'londonEye'
  | 'stPauls'
  | 'shard' // the tapering glass spike over London Bridge Quarter
  | 'gherkin' // the lattice-wrapped glass bullet in The City
  | 'towerBridge' // drive-through gateway: towers flank the road, walkways span overhead
  | 'towerOfLondon'
  // Napoli
  | 'vesuvio' // Vesuvius across the bay — twin-peaked, smoking
  | 'castelDellOvo' // the yellow-tufa sea fortress on its islet
  | 'castelNuovo' // dark keep + round towers + the white triumphal arch
  | 'plebiscito' // San Francesco di Paola: dome + sweeping colonnade arms
  | 'galleria' // drive-through glass barrel-vault gateway at the finish
  // Tokyo
  | 'shibuyaTower' // cylindrical mall tower wrapped in neon rings
  | 'kaminarimon' // drive-through temple gate with the giant red lantern
  | 'pagoda' // five-storey pagoda
  | 'tokyoTower' // the orange-and-white lattice tower, lit
  | 'fuji' // Mount Fuji's moonlit snow cap on the horizon
  | 'torii' // giant vermilion torii gateway at the finish
  // Rio
  | 'sugarloaf' // the granite domes + cable car across the bay
  | 'arcosDaLapa' // drive-through white two-tier aqueduct arches
  | 'christRedeemer' // Christ the Redeemer on his green peak
  | 'maracana' // the great stadium bowl behind the finish
  // Dakar
  | 'grandeMosquee' // white mosque, green dome, tall minaret
  | 'goreeHouse' // ochre colonial house with the white balcony
  | 'renaissanceMonument' // the colossal bronze family on its hill
  | 'giantBaobab' // one ancient baobab, alone by the road
  | 'rallyArch' // the rally-style finish gantry over the line
  // Lagos
  | 'lagoonLighthouse' // striped lighthouse on its islet
  | 'nationalTheatre' // the great concrete 'military cap' hall
  | 'cocoaHouse' // the proud brown high-rise
  | 'lekkiIkoyi' // cable-stayed bridge — drive under the fanning cables
  // Mumbai
  | 'cst' // the great Gothic railway terminus
  | 'seaLink' // the Bandra-Worli cable bridge on the sea horizon
  | 'hajiAliDargah' // the white shrine out on the water
  | 'tajHotel' // the red-domed grand hotel
  | 'gatewayOfIndia' // the basalt arch — the finish passes through it
  // Patagonia
  | 'estanciaGate' // the wooden ranch gate over the road at the start
  | 'glacierWall' // the long blue ice wall (Perito Moreno)
  | 'fitzRoy'; // the granite massif towering over the finish

/** Where to drop a landmark: `at` = progress 0..1, `side` = -1 left / 0 centre / 1 right. */
export interface Landmark {
  kind: LandmarkKind;
  at: number;
  side: -1 | 0 | 1;
}

/**
 * A centred launch ramp (NYC's Times Square "red stairs"): cars + balls that take
 * the middle of the road ride up it and launch; there's clear road on either side
 * to pass without jumping. `at` = progress 0..1 of the ramp's start.
 */
export interface RampSpec {
  at: number;
  length: number; // incline length along the track (units)
  halfWidth: number; // lateral half-extent of the ramp — leave road on both sides
  height: number; // launch height at the top lip
  /** Visual style (physics is identical): body colour (default NYC ruby red). */
  color?: number;
  /** Visual style: emissive glow colour (default deep red). */
  emissive?: number;
  /** Visual style: number of step tiers (default 7; 1 = a smooth wedge/dune). */
  steps?: number;
  /** Visual BUILD (physics is identical — GS5 ramp identity pass): every
   *  launcher reads as a real city object, never a flat-colour slab.
   *   'jetty'  — Lagos' planked timber pier on piles (M117d, the material bar)
   *   'bus'    — London: a tilted parked double-decker; you ride its roof
   *   'sand'   — Rio: sand-grain wedge with heaped-fill berm skirts
   *   'dune'   — Dakar: sand + wind-ripple lines + irregular berms
   *   'basalt' — Mumbai: dark basalt steps, tread nosings + reflector studs
   *              (daylight-proof cues instead of emissive rails)
   *   'tufa'   — Napoli: tufa block steps, worn nosings, warm stone rail caps
   *   'neon'   — Tokyo: dark metal body, glow confined to rails + lip bar
   *   'snow'   — Patagonia: heaped-snow shell with drift skirts
   *  Default = NYC's red stairs (glass-stair gloss, faint red emissive). */
  build?: 'jetty' | 'bus' | 'sand' | 'dune' | 'basalt' | 'tufa' | 'neon' | 'snow';
}

/**
 * Interactive course features (M47) — the city-themed gameplay furniture ON the
 * road: BOOST pads (a strip that surges your engine for ~a second), SLICK
 * patches (ice / monsoon puddles / sand drifts — grip drops, you slide),
 * static OBSTACLES (parked taxis, rocks, market crates — cars and balls bump
 * off them), and FORDS (nyc-lake: open water running ACROSS the road, with a
 * plank crossing through the middle — miss the plank and you wade).
 * Hand-placed per city like landmarks, so placement is deterministic
 * by definition (offline + online identical). Obstacles are kept NEAR THE
 * EDGES (|lateral| ≥ 8) so the AI's racing line stays clear; pads sit on the
 * line as a reward. Physics in `src/physics/trackFeatures.ts`.
 */
export type TrackFeatureKind = 'boost' | 'slick' | 'obstacle' | 'ford';

/** Visual flavour of a feature (physics is identical per kind). */
export type TrackFeatureStyle =
  | 'billboard' // gold chevron pad (NYC Broadway, Napoli lungomare)
  | 'neon' // cyan chevron pad (Tokyo)
  | 'buslane' // red bus-lane pad (London)
  | 'brt' // danfo-yellow BRT pad (Lagos)
  | 'drums' // green-and-gold carnival pad (Rio)
  | 'puddle' // dark reflective rain water
  | 'ice' // pale glacial sheen
  | 'sand' // tan drift blown across the road
  | 'steam' // NYC subway-grate mist
  | 'taxi' // parked yellow cab
  | 'rock' // fallen boulder
  | 'crate' // market crate stack
  | 'barrier' // striped roadworks barrier
  | 'cone' // traffic cone
  | 'scooter' // parked Vespa cluster
  | 'lake'; // nyc-lake: The Lake fording the road under Bow Bridge

export interface TrackFeatureSpec {
  kind: TrackFeatureKind;
  /** Progress 0..1 of the feature's centre along the course. */
  at: number;
  /** Signed offset from the centreline (road half-width is 18). */
  lateral: number;
  /** boost/slick/ford: extent ALONG the track (units). */
  length?: number;
  /** boost/slick/ford: lateral half-width (units). */
  halfWidth?: number;
  /** obstacle: collision circle radius (units). */
  radius?: number;
  /** ford ONLY — the PLANK CROSSING: the lateral centre of the dry timber
   *  window through the water, as an offset from the feature's own `lateral`.
   *  Defaults to 0 (a plank laid down the middle of the road). */
  plankLateral?: number;
  /** ford ONLY — half-width of that dry window (units). A body whose CENTRE is
   *  inside it crosses dry; anything wider of it wades. Default 6. */
  plankHalfWidth?: number;
  style: TrackFeatureStyle;
}

/** Visual flavour of a MOVING obstacle (M99) — physics is the same capsule
 *  shuttle for all; the style picks the city-true mesh + sizing defaults. */
export type MoverStyle =
  | 'cab' // NYC yellow taxi
  | 'bus' // London double-decker
  | 'vespa' // Napoli scooter + rider
  | 'keicab' // Tokyo night cab, lit roof sign
  | 'beachball' // Rio giant beach ball rolling across
  | 'goats' // Dakar — a little line of goats
  | 'danfo' // Lagos yellow minibus
  | 'rickshaw' // Mumbai auto-rickshaw
  | 'guanaco'; // Patagonia guanacos

/**
 * A MOVING course obstacle (M99): city traffic that shuttles ACROSS the road
 * on a fixed timetable — crosses between the two edges (±span) with a parked
 * DWELL at each side, so there's always a safe window to time. Fully
 * deterministic from the race clock (physics in `src/physics/movers.ts`), so
 * offline + every online client agree with zero extra sync.
 */
export interface MoverSpec {
  /** Progress 0..1 of the crossing point along the course. */
  at: number;
  style: MoverStyle;
  /** Crossing half-extent (dwells at ±span; road half-width is 18). */
  span?: number;
  /** Crossing speed (u/s) while moving. */
  speed?: number;
  /** Seconds parked at each edge between crossings. */
  dwell?: number;
  /** Collision capsule half-length along the facing axis (default per style). */
  halfLen?: number;
  /** Collision capsule radius (default per style). */
  radius?: number;
  /** Phase offset in seconds into the cycle (staggers paired movers). */
  phase?: number;
}

/** PHYSICS flavour of a SECRET SHORTCUT (M117c aerial rework — the owner's
 *  rule: ALL shortcuts launch from kicker ramps into elevated paths; no
 *  tunnels, no ground-level entrances). Batch 1: NYC + Lagos (+ Rio's cavern,
 *  parked for its redo); batch 2 reuses 'highline' physics for Tokyo +
 *  Napoli — their city identity lives in `look`, never in new physics. */
export type ShortcutStyle =
  | 'cavern' // Rio: a side kicker fires you at a cavern mouth up the Tijuca cliff
  | 'highline' // side kicker → an elevated line beside the course (NYC/Tokyo/Napoli)
  | 'skypath'; // Lagos: the city's own (enlarged) ramp — catch the deck over the lagoon

/** VISUAL dressing of a shortcut deck (M117 batch 2). Physics per `style` is
 *  byte-identical whatever the look — the sim NEVER branches on this; it only
 *  picks which city builder skins the deck in shortcutMeshes.ts.
 *   'park'       — NYC: High-Line concrete, planters, steel columns (default
 *                  for 'highline' when a spec says nothing).
 *   'expressway' — Tokyo: the abandoned elevated expressway spur.
 *   'rooftop'    — Napoli: the Spaccanapoli rooftop run. */
export type ShortcutLook = 'park' | 'expressway' | 'rooftop';

/**
 * A SECRET SHORTCUT (M117c): a ramp-launched AERIAL cut. Hit the launch ramp
 * with real speed and the right line — ball on your nose — and you FLY onto
 * an elevated deck you then drive yourself, rejoining the course down a
 * descent chute at `exitAt`. Come in slow or off-line and you thud back
 * down; the deck simply doesn't catch you. Humans only (decks are
 * uncatchable for bots — pace ladder untouched, matchmade seats can never
 * reveal themselves mid-air).
 * Physics in `src/physics/shortcuts.ts`; feel knobs in `tuning.shortcuts`.
 */
export interface ShortcutSpec {
  style: ShortcutStyle;
  /** VISUAL identity only (M117 batch 2): which city builder dresses the deck.
   *  Defaults per style in placeShortcuts (highline → 'park'). Physics is
   *  identical per `style` — nothing in the sim may ever branch on this. */
  look?: ShortcutLook;
  /** Preferred launch fraction 0..1. Side-kicker styles slide it ± refineSpan
   *  to the straightest measured RUNWAY (the placement lesson, encoded); the
   *  'skypath' style anchors to the city's own ramp instead. */
  at: number;
  /** Progress 0..1 where the descent chute rejoins the course. */
  exitAt: number;
  /** Which side of the course the aerial path rides (−1 or +1). */
  side: -1 | 1;
  /** Outward BULGE of the path at its midpoint (units) — through the tower
   *  block, out over the lagoon, into the hillside. */
  bow?: number;
  /** Deck floor height override (defaults per style in tuning.shortcuts). */
  mouthLift?: number;
}

/** Everything course-shaped a race sim needs beyond the Track itself — the
 *  launch ramp + the interactive features + the moving traffic + the secret
 *  shortcuts. One bundle so the ramp plumbing didn't need a second parameter
 *  everywhere (M47). */
export interface CourseExtras {
  ramp?: RampSpec;
  features?: TrackFeatureSpec[];
  movers?: MoverSpec[];
  shortcuts?: ShortcutSpec[];
}

/** The course extras bundle for a city (what Race/SimCore consume). */
export function courseExtras(city: City): CourseExtras {
  return {
    ramp: city.ramp,
    features: city.features,
    movers: city.movers,
    shortcuts: city.shortcuts,
  };
}

/**
 * Per-city WATER dressing (GS1 — the merged shoreline-water rebuild). All of
 * this is DISPLAY tuning consumed by scenery/water.ts + builders.buildWater;
 * nothing in the sim reads it. Omit the whole block for the shared defaults
 * (an urban quay edge with the standard ripple material).
 */
export interface WaterTune {
  /** Shore dressing where water meets land: 'quay' = hard urban edge (no
   *  blend band), 'beach' = white surf-foam line over the sand, 'salt' = the
   *  pale crust rim of Lac Rose, 'mud' = a warm lagoon mud bank, 'glacial' =
   *  a milky rim. Default 'quay'. */
  shore?: 'quay' | 'beach' | 'salt' | 'mud' | 'glacial';
  /** Ripple density multiplier for the big water bodies (1 = the shared
   *  default; <1 = broader, calmer wind lanes). Small ponds auto-densify. */
  ripple?: number;
  /** Ripple normal-map strength (default 0.55). */
  normalScale?: number;
  /** Water surface roughness (default 0.14 — mirror-calm). */
  roughness?: number;
  /** Sky-reflection boost (default 1.6). */
  envMapIntensity?: number;
  /** Remap emitted water colours at build time (e.g. Patagonia's swimming-pool
   *  cyan → milky glacial turquoise) without touching the zone palettes. */
  recolor?: { from: number; to: number }[];
  /** Extra derived organic lakes (NYC: the Bow Bridge lake composition).
   *  `at` = progress 0..1, `lateral` signed units off the centreline. */
  lakes?: { at: number; lateral: number; r: number; color: number }[];
}

export interface City {
  id: string;
  name: string;
  country: string;
  /** Visual palette (sky/fog/light/surface tints) — the time-of-day + locale mood. */
  theme: TrackTheme;
  /** Per-city track shape: curve sharpness + section length (twistiness). */
  track: TrackConfig;
  /** AI rival difficulty for this city (ramps up across the Tour). */
  difficulty: DifficultyLevel;
  /** Fixed seed so the city always generates the same course. */
  seed: number;
  /** Real-world coordinates (for the M9 flying-globe transition). */
  lat: number;
  lon: number;
  /** Roadside scenery zones along the route (omit → a generic cityscape). */
  route?: RouteZone[];
  /** Unique hero landmarks dropped at specific points along the route. */
  landmarks?: Landmark[];
  /** A centred launch ramp on the course (NYC: the Times Square red stairs). */
  ramp?: RampSpec;
  /** Interactive course features (M47): boost pads, slicks, obstacles. */
  features?: TrackFeatureSpec[];
  /** Moving obstacles (M99): city traffic crossing the road on a timetable. */
  movers?: MoverSpec[];
  /** Secret shortcuts (M116): hidden precision-gated tunnels/alleys/jumps. */
  shortcuts?: ShortcutSpec[];
  /** Per-city water dressing (GS1) — display only, see WaterTune. */
  water?: WaterTune;
}

/** A track config = the shared defaults with per-city curve/twistiness overrides. */
function track(turnCurvatureMax: number, sectionLenMin: number, sectionLenMax: number): TrackConfig {
  return { ...tuning.track, turnCurvatureMax, sectionLenMin, sectionLenMax };
}

export const CITIES: City[] = [
  {
    // 1 — NEW YORK · golden dusk. Route: Harlem → Central Park → Broadway → Times Sq.
    id: 'nyc',
    name: 'New York',
    country: 'USA',
    theme: {
      name: 'New York',
      background: 0x232a46,
      sky: { top: 0x1c2750, horizon: 0xe8a878 }, // golden but softer, less neon-orange
      fog: 0x9a9eb4, // cool neutral dusk haze — wider tonal range
      sun: 0xffe6cc, // soft warm light, well back from orange
      sunIntensity: 1.8,
      hemiSky: 0xc8d8f0, // strong cool ambient fill to balance the warm sun
      hemiGround: 0x2a2e42, // cool shadow fill for depth
      hemiIntensity: 1.25,
      road: 0x2b2c33,
      roadLine: 0xffd24a,
      ground: 0x363b4a, // slightly cooler ground so the warm sun reads less orange
      wall: 0x49b8ff,
      wallStyle: 'jersey',
      roadStyle: 'usDouble',
      // Graphics overhaul (premium-stylized realism): a real dusk sky (Qwantani
      // Dusk, CC0 Poly Haven) now lights + reflects the whole scene, and a genuine
      // cracked-tarmac road catches that warm light instead of a flat tint.
      env: { hdri: 'env/qwantani_dusk_2.jpg', rotationDeg: 40, backgroundBlur: 0.04 },
      roadTex: { base: 'textures/asphalt_02', repeat: [6, 1.6], normalScale: 0.85, roughness: 0.9 },
    },
    track: track(0.06, 14, 34),
    difficulty: 'easy',
    seed: 1971,
    lat: 40.71,
    lon: -74.01,
    route: [
      { until: 0.28, kind: 'brownstones', label: 'Harlem' },
      { until: 0.6, kind: 'park', label: 'Central Park' },
      { until: 0.86, kind: 'skyscrapers', label: 'Broadway' },
      { until: 1.0, kind: 'timessquare', label: 'Times Square' },
    ],
    landmarks: [
      // Harlem
      { kind: 'apollo', at: 0.09, side: 1 },
      { kind: 'hamiltonGrange', at: 0.16, side: -1 },
      { kind: 'langstonHughes', at: 0.22, side: 1 },
      // Central Park (Bow Bridge spans the road over water)
      { kind: 'belvedereCastle', at: 0.4, side: -1 },
      { kind: 'bowBridge', at: 0.5, side: 0 },
      { kind: 'bethesdaFountain', at: 0.56, side: -1 },
      // Midtown rises along Broadway: the Chrysler's crown, then the Empire State.
      // BOTH stand on the driver's RIGHT (owner: "the Chrysler is hard to see on
      // the left going into that bend — move it across the street"). MEASURED,
      // not assumed: with the chase cam world +X renders on the LEFT (§6.2), so a
      // landmark at `side: 1` projects to screen x −0.77 and `side: -1` to +1.12.
      // side −1 IS the driver's right. The pair also gets a forecourt clearing in
      // scenery/zones.ts (MIDTOWN_HERO_CLEAR) — the generic Broadway towers stand
      // at lateral 20-42, i.e. squarely in front of both of them.
      { kind: 'chrysler', at: 0.7, side: -1 },
      { kind: 'empireState', at: 0.79, side: -1 },
      // Times Square — the New Year's Eve ball tower behind the finish
      { kind: 'nyeBall', at: 1.0, side: 0 },
    ],
    // TIMES SQUARE RED STAIRS — a centred launch ramp into the finish.
    //
    // nyc-lake (owner: "make the center part of the TKTS stairs the same height
    // as the adjusted height you made the sides, and increase the jump physics
    // respectively"). The previous round raised only the flanking WINGS, to
    // wingRise 2.8 × height = 8.4, and deliberately left `height` alone because
    // it is PHYSICS. The owner now wants the centre run up there too, so this
    // IS the sim change and SIM_VERSION moves with it.
    //
    // `height` 8.4 is the wings' measured crest, taken literally. `length` 9 →
    // 17 is NOT decoration: slope = height/length is the launch, and 8.4 over 9
    // is a 43° face — which `rampHeight` would render as a magic elevator (it
    // sets y directly, it does not resist), and the stairs would read as a
    // climbing wall. 8.4/17 = 26.3°, which is both drivable and what the real
    // Duffy Square steps actually are. `steps` 7 → 11 keeps the tread rhythm
    // sane over the longer run (1.55 u going, 0.76 u rise); it is VISUAL only
    // (buildRampSteps), never physics.
    //
    // MEASURED, full-throttle entry (the numbers the owner will feel):
    //   before  launch 44.7 u/s → apex  4.0 u over the road, 1.35 s air,  57 u flown
    //   after   launch 43.8 u/s → apex 17.4 u over the road, 2.13 s air,  90 u flown
    // The lip sits at arc ≈ 1990 of 2192 and the landing at ≈ 2080 — still 56 u
    // clear of the first finish-plaza stand, and the M117d kerb cabs (|lateral|
    // ≥ 10, arcs 1947-2006) are flown straight over.
    ramp: { at: 0.9, length: 17, halfWidth: 5, height: 8.4, steps: 11 },
    // Yellow cabs double-parked at the kerbs, a subway grate venting steam,
    // and a gold billboard pad on the Broadway straight (M99: a real runway).
    features: [
      { kind: 'obstacle', at: 0.24, lateral: -9, radius: 2, style: 'taxi' },
      // (M117c: the steam grate moved off the 47% mark — the High Line kicker
      // needs that stretch of kerb; the park's damp patch reads fine earlier.)
      { kind: 'slick', at: 0.435, lateral: 4, length: 7, halfWidth: 3.5, style: 'steam' },
      // M117d ROADWORKS at the High Line launch (owner: "make the ramp look
      // like an obstacle construction zone but operate like a ramp"). A real
      // lane-closure taper down the LEFT kerb — cones then a barrier — that
      // reads "avoid" and quietly funnels you onto the steel plate. All of it
      // sits at |lateral| 15+, outboard of the plate's 4..14 footprint, so it
      // narrows the kerb lane without ever blocking the launch itself; the
      // AI races the centre line and never meets it.
      { kind: 'obstacle', at: 0.4545, lateral: 16.5, radius: 1.5, style: 'cone' },
      { kind: 'obstacle', at: 0.46, lateral: 15, radius: 1.5, style: 'cone' },
      { kind: 'obstacle', at: 0.4662, lateral: 16.2, radius: 1.8, style: 'barrier' },
      // nyc-lake THE BOW BRIDGE FORD (owner: "have the water flowing under the
      // bridge on the course as well with a make-shift plank bridge crossing
      // the drivers and their balls over the water that is under the bridge on
      // the course. And if the racers don't go over the plank area then they
      // will slow down crossing the water portion under the bridge").
      //
      // `at` 0.5 = Bow Bridge's own mark, so The Lake genuinely runs UNDER the
      // span. halfWidth 24 overshoots the 18 u half-road on purpose: the band
      // has to reach past the guardrail line and into both authored lakes
      // (their inner reach sits around lateral 22-26), or a dry sliver would
      // open between the ford and The Lake and break the "one body of water"
      // read. The extra width costs nothing — a car cannot be out there.
      //
      // THE PLANK IS CENTRED, and that is a deliberate design choice, not a
      // default: a make-shift crossing gets built on the path people already
      // take, it makes the ford a reward for HOLDING THE LINE rather than a
      // toll everyone pays every lap, and it keeps the AI (which races the
      // centre) out of the water — the M47 rule that the racing line stays
      // clear, and what keeps the locked pace ladder honest. plankHalfWidth 6
      // = a 12 u deck on a 36 u road: a third of the width, so getting shoved
      // wide in a pack, or knocking your ball off-line, genuinely wets you.
      { kind: 'ford', at: 0.5, lateral: 0, length: 22, halfWidth: 24, plankHalfWidth: 6, style: 'lake' },
      { kind: 'boost', at: 0.68, lateral: 0, length: 22, halfWidth: 4, style: 'billboard' },
      { kind: 'obstacle', at: 0.78, lateral: 10, radius: 2, style: 'taxi' },
      // M117d TIMES SQUARE TRAFFIC (owner: "add a few traffic obstacles on
      // the left and right of the red staircase so that it adds more of an
      // incentive to try and jump the red ramp instead of going on the sides
      // of it"). The stairs span |lateral| ≤ 5; these double-parked cabs
      // stagger down both kerb lanes so going around costs you a weave while
      // the ramp stays the clean, fast line. Kept at |lateral| ≥ 9 per the
      // M47 rule so the racing line — and the AI — is untouched.
      { kind: 'obstacle', at: 0.888, lateral: -10, radius: 2, style: 'taxi' },
      { kind: 'obstacle', at: 0.897, lateral: 10.5, radius: 2, style: 'taxi' },
      { kind: 'obstacle', at: 0.906, lateral: -14.5, radius: 2, style: 'taxi' },
      { kind: 'obstacle', at: 0.915, lateral: 14, radius: 2, style: 'taxi' },
    ],
    // M99: crosstown cabs — one at the park edge, one weaving off Broadway.
    movers: [
      { at: 0.33, style: 'cab', speed: 6.5, dwell: 3 },
      { at: 0.55, style: 'cab', speed: 6.5, dwell: 3.2, phase: 7 },
    ],
    // M117c SECRET SHORTCUT — the PARK HIGH LINE (owner spec: "the ramp
    // before the bridge in central park should allow a user to reach a
    // shortcut 'highline' that goes through the initial skyscrapers on the
    // left side of the course"). Measured on this seed: the 39–47% stretch
    // is a 188 u DEAD-straight runway ending at the kicker just before Bow
    // Bridge (0.5); a committed launch (~34+) catches the elevated line on
    // the player's LEFT, which rides past the bridge and through the first
    // Midtown towers — cutting the 54–62% bends for ≈ +1.1 s — then chutes
    // back down at ~65%.
    // (at 0.478: the crossing sits ~50 u BEFORE Bow Bridge, so the aim-line
    // has walked the deck out past the bridge's ±33 u stone banks by the time
    // it passes — at 0.487 the deck flew straight through the masonry,
    // measured. M117c: the deck's leading edge now stands 10 u BEYOND the
    // wall among the park trees — a long jump to a tucked-away landing.)
    // (M117e: mouthLift 7.6 over the shared 6.3 — with the ramp pushed back
    // this park line still let a 26 u/s crawl in; a higher sill puts its
    // entry bar with the rest of the set at ~36 u/s. Measured.)
    shortcuts: [{ style: 'highline', at: 0.478, exitAt: 0.655, side: 1, bow: 16, mouthLift: 7.6 }],
    // THE LAKE — nyc-lake round, and by the owner's rule the ONLY water in
    // Central Park: "the only water is supposed to be 'The Lake' that 'The Bow
    // Bridge' is meant to cross over". The park builder leaves the ground bare
    // at the bridge so it shows, the reservoir the previous round spread over
    // the whole apron is gone (NYC emits zero course water), and the FORD above
    // carries the same body of water ACROSS the road under the span.
    //
    // ONE BODY, NOT TWO DISCS. Both lobes now sit at the SAME `at` as the
    // bridge and the ford, so the three line up on one axis instead of the old
    // 0.5/0.508 stagger that read as two puddles either side of a dry road.
    // ±52 is the closest they can legally sit: planPonds pushes a disc until
    // |lateral| − r ≥ halfWidth + 1.5 (= 19.5), i.e. ≥ 49.5 for the r-30 lobe.
    // The ford's own 24 u half-band then overlaps both lobes' inner reach.
    // 'mud' shore: a soft jittered bank with a warm wet-earth tint — a park
    // lake edge. 'quay' (the default) would draw a razor-straight 700 u line,
    // which is the artificial read this round exists to remove.
    water: {
      shore: 'mud',
      envMapIntensity: 2.0,
      lakes: [
        { at: 0.5, lateral: -52, r: 30, color: 0x2f5f74 },
        { at: 0.5, lateral: 52, r: 26, color: 0x2f5f74 },
      ],
    },
  },
  {
    // 2 — LONDON · overcast grey-blue morning, cream lines, red-bus accent.
    id: 'london',
    name: 'London',
    country: 'UK',
    theme: {
      name: 'London',
      background: 0xaab6c4,
      sky: { top: 0x8b9bb2, horizon: 0xc7d0d8 },
      // MEASURED: fog must sit at or below the sky it is seen against, or far
      // geometry converges to a plateau BRIGHTER than the backdrop and reads as
      // pale cardboard cut-outs. At 0xc3cdd6 fogged slabs measured 148-188 vs a
      // 126-157 sky right behind them (a fully-fogged landmark GLB hit 166 vs
      // 141) — which is why London's skyline silhouettes read as paper, one of
      // them dead centre in the start-straight vanishing point. Theme.ts:63's
      // rule ("fog matches the sky horizon so they blend") was simply not held
      // here. 0xa8b4c2 sits just under the 0x8b9bb2 sky top and blends.
      fog: 0xa8b4c2,
      sun: 0xdfe6ee,
      sunIntensity: 1.5,
      hemiSky: 0xc2ccd6,
      hemiGround: 0x36392f,
      hemiIntensity: 1.15,
      road: 0x34353b,
      roadLine: 0xe8e2c8,
      ground: 0x4c5740,
      wall: 0xd23357,
      wallStyle: 'balustrade',
      roadStyle: 'ukLanes',
      // Graphics overhaul: real overcast London sky lights the scene flat + soft;
      // the asphalt reads faintly damp under the cloud.
      env: { hdri: 'env/kloofendal_overcast.jpg', rotationDeg: 30, backgroundBlur: 0.03 },
      roadTex: { base: 'textures/asphalt_02', repeat: [6, 1.6], normalScale: 0.85, roughness: 0.8 },
    },
    track: track(0.07, 12, 32),
    difficulty: 'easy',
    seed: 1666,
    lat: 51.51,
    lon: -0.13,
    // A run down the Thames, west → east: Westminster → South Bank → The City →
    // through Tower Bridge to the line.
    route: [
      { until: 0.28, kind: 'westminster', label: 'Westminster' },
      { until: 0.55, kind: 'thames', label: 'South Bank' },
      { until: 0.85, kind: 'skyscrapers', label: 'The City' },
      { until: 1.0, kind: 'thames', label: 'Tower Bridge' },
    ],
    landmarks: [
      { kind: 'bigBen', at: 0.12, side: -1 }, // Westminster riverfront
      { kind: 'londonEye', at: 0.42, side: 1 }, // across the river, South Bank
      { kind: 'shard', at: 0.58, side: 1 }, // rising over the south bank
      { kind: 'stPauls', at: 0.7, side: -1 }, // The City
      { kind: 'gherkin', at: 0.8, side: -1 }, // deeper into The City
      { kind: 'towerOfLondon', at: 0.93, side: -1 }, // land side, beside the bridge
      { kind: 'towerBridge', at: 1.0, side: 0 }, // drive-through gateway at the finish
    ],
    // A double-decker's open top deck as a South Bank launch ramp (M47).
    ramp: { at: 0.5, length: 9, halfWidth: 5, height: 3, color: 0xc41e2f, emissive: 0x5a0a10, steps: 2, build: 'bus' },
    // A red bus-lane pad, rain on the embankment, roadworks in The City.
    features: [
      { kind: 'boost', at: 0.32, lateral: -5, length: 22, halfWidth: 3.5, style: 'buslane' },
      { kind: 'slick', at: 0.62, lateral: 3, length: 9, halfWidth: 4, style: 'puddle' },
      { kind: 'obstacle', at: 0.75, lateral: 10, radius: 1.8, style: 'barrier' },
      { kind: 'obstacle', at: 0.88, lateral: -9, radius: 1.8, style: 'barrier' },
    ],
    // M99: a double-decker crossing a junction — huge, slow, unmissable.
    movers: [{ at: 0.42, style: 'bus', speed: 5, dwell: 3.5, halfLen: 3.6, radius: 1.5 }],
    // (M117c: London's ground-level arches are retired per the owner's
    // aerial-only rule — its ramp-launched shortcut arrives in a later batch.)
    // GS1 — a calmer Thames: softer ripple so the overcast river reads as
    // slow grey water instead of white sparkle noise from elevated views.
    water: { normalScale: 0.48, roughness: 0.2 },
  },
  {
    // 3 — NAPOLI · vivid Mediterranean midday, azure sky, Vesuvius terracotta.
    id: 'napoli',
    name: 'Napoli',
    country: 'Italy',
    theme: {
      name: 'Napoli',
      background: 0x9fd6f2,
      sky: { top: 0x1f7fd6, horizon: 0xa6daf4 },
      fog: 0xbfe2f5,
      sun: 0xfff4d0,
      sunIntensity: 2.8,
      hemiSky: 0x9fd2f0,
      hemiGround: 0x8a6a48, // warm bounced light — Mediterranean shade stays luminous
      hemiIntensity: 1.3,
      road: 0x3a3a40,
      roadLine: 0xf2d35e,
      ground: 0x7a9a4a,
      wall: 0xff7a3c,
      wallStyle: 'tufa',
      roadStyle: 'cobble',
      // Graphics overhaul: vivid clear Mediterranean blue lights the pastels;
      // dry sun-baked tarmac.
      env: { hdri: 'env/kloofendal_43d_clear.jpg', rotationDeg: 40, backgroundBlur: 0.02 },
      roadTex: { base: 'textures/asphalt_02', repeat: [6, 1.6], normalScale: 0.85, roughness: 0.92 },
    },
    track: track(0.085, 12, 30),
    difficulty: 'medium',
    seed: 79,
    lat: 40.85,
    lon: 14.27,
    // Old town → the bay (Vesuvius smoking across the water) → castle quarter →
    // the grand piazza, finishing INSIDE the Galleria's glass vault.
    route: [
      { until: 0.3, kind: 'spaccanapoli', label: 'Spaccanapoli' },
      { until: 0.62, kind: 'bayfront', label: 'Lungomare' },
      { until: 0.86, kind: 'castello', label: 'Castel Nuovo' },
      { until: 1.0, kind: 'piazza', label: 'Piazza del Plebiscito' },
    ],
    landmarks: [
      { kind: 'castelDellOvo', at: 0.4, side: 1 }, // out on the bay water
      { kind: 'vesuvio', at: 0.52, side: 1 }, // far across the bay
      { kind: 'castelNuovo', at: 0.72, side: -1 },
      { kind: 'plebiscito', at: 0.9, side: -1 },
      { kind: 'galleria', at: 1.0, side: 0 }, // finish under the glass dome
    ],
    // Vespas parked where they please (of course), a gold pad on the lungomare.
    features: [
      { kind: 'obstacle', at: 0.2, lateral: -8, radius: 1.6, style: 'scooter' },
      { kind: 'obstacle', at: 0.34, lateral: 9, radius: 1.6, style: 'scooter' },
      { kind: 'boost', at: 0.5, lateral: 0, length: 20, halfWidth: 4, style: 'billboard' },
    ],
    // M99: a tufa-stone stair wedge off the lungomare — jump toward the castles.
    ramp: { at: 0.62, length: 9, halfWidth: 5, height: 3, color: 0xd8c07a, emissive: 0x3a2c10, steps: 4, build: 'tufa' },
    // M99: Vespas darting across the alleys (of course) — zippy but small.
    movers: [
      { at: 0.27, style: 'vespa', speed: 7.5, dwell: 2.5, halfLen: 1.1, radius: 0.9 },
      { at: 0.8, style: 'vespa', speed: 7.5, dwell: 2.8, halfLen: 1.1, radius: 0.9, phase: 6 },
    ],
    // M116 SECRET SHORTCUT — the VICOLO: Spaccanapoli's tightest alley, laundry
    // overhead. SLOW gate (max 22) — charge the mouth flat-out and you simply
    // roll past; ease off, tuck the ball in, and squeeze through.
    // (M116 vicolo retired — replaced by the batch-2 aerial cut below.)
    // M117 batch-2 SECRET SHORTCUT — the ROOFTOPS OF SPACCANAPOLI. The old
    // town's opening straight (7.5–8.7%) is DEAD straight on this seed
    // (runway curvature 0.000, measured) and ends at a kicker on the
    // player's LEFT; commit (~34+, the ramp boost carries you) and you loft
    // onto a rooftop run over the alleys. The route below swings a real 46°
    // through the quarter — the deck flies the direct line, drifting up to
    // ~190 u inside the city block while the road loops around — then chutes
    // down at 30%, right where the route opens toward the sea. Falls 7.7 u
    // end to end (2.6% average — a dead-stopped ball rolls itself out in
    // 5–45 s anywhere on the deck, measured); worth ≈ +4.6 s at perfect
    // execution. Measured clear: no landmarks
    // anywhere near the window; scooter@0.2 + the vespa mover@0.27 keep
    // 66+ u off both anchors. `look: 'rooftop'` is VISUAL only — the deck
    // physics is NYC's highline, byte for byte.
    // (M117e: mouthLift 7.6 over the shared 6.3 — same reason as NYC's, and
    // the taller sill lifts the rooftop run to a believable third-storey
    // height over the old town. Measured entry bar ~36 u/s.)
    shortcuts: [{ style: 'highline', look: 'rooftop', at: 0.087, exitAt: 0.3, side: 1, bow: 14, mouthLift: 7.6 }],
  },
  {
    // 4 — TOKYO · neon night. Dark wet roads, hot-pink line, cyan rails. Drama.
    id: 'tokyo',
    name: 'Tokyo',
    country: 'Japan',
    theme: {
      name: 'Tokyo',
      background: 0x0a0a1f,
      sky: { top: 0x080814, horizon: 0x2a1a3a },
      fog: 0x191334,
      // M104b (owner): the COURSE was too dark — cars and road read as
      // silhouettes against the neon. Lift the LIGHTS (cool moonlight sun +
      // ambient) and the road/ground albedo toward NYC's readable levels,
      // while the SKY/background/fog stay the untouched night. The light
      // colours stay cool blue so it still reads as night, just a lit one.
      sun: 0x8898c8,
      sunIntensity: 1.5,
      hemiSky: 0x4a5080,
      hemiGround: 0x241c30,
      hemiIntensity: 1.1,
      road: 0x2a2b36,
      roadLine: 0xff2d7a,
      ground: 0x1c1c30,
      wall: 0x00e5ff,
      wallStyle: 'neon',
      roadStyle: 'jpTech',
      // M158c (owner): Tokyo's streets are lit at night — warm pools under
      // kerb lamps every 6 nodes (24 m) plus an even wash; the sky stays night.
      streetLights: { color: 0xffd6a0, strength: 0.95, ambient: 0.08, spacing: 6 },
      // Graphics overhaul: dark night sky with a city-glow horizon; a low-roughness
      // RAIN-SLICKED road so the neon night glistens. (backgroundIntensity stays
      // 0.3 — the brighter IBL lights surfaces, never the visible sky.)
      env: { hdri: 'env/qwantani_night.jpg', rotationDeg: 110, backgroundBlur: 0.06, intensity: 0.6, backgroundIntensity: 0.3 },
      roadTex: { base: 'textures/asphalt_02', repeat: [6, 1.6], normalScale: 0.7, roughness: 0.38 },
    },
    track: track(0.095, 10, 28),
    difficulty: 'medium',
    seed: 1964,
    lat: 35.68,
    lon: 139.69,
    // Electric Shibuya → lantern-lit temple town → Tokyo Tower → the bay,
    // a neon launch ramp, and a giant torii over the finish. Fuji watches.
    route: [
      { until: 0.35, kind: 'shibuya', label: 'Shibuya' },
      { until: 0.6, kind: 'asakusa', label: 'Asakusa' },
      { until: 0.85, kind: 'minato', label: 'Minato' },
      { until: 1.0, kind: 'bayfinish', label: 'Tokyo Bay' },
    ],
    landmarks: [
      { kind: 'shibuyaTower', at: 0.12, side: 1 },
      { kind: 'kaminarimon', at: 0.45, side: 0 }, // drive under the great lantern
      { kind: 'pagoda', at: 0.54, side: -1 },
      { kind: 'tokyoTower', at: 0.72, side: -1 },
      { kind: 'fuji', at: 0.93, side: 1 }, // far across the bay
      { kind: 'torii', at: 1.0, side: 0 }, // vermilion gateway over the line
    ],
    // A glowing launch pad on the bayfront straight — jump, then thread the torii.
    ramp: { at: 0.875, length: 9, halfWidth: 5, height: 3, color: 0x16181f, emissive: 0x18d0ff, steps: 5, build: 'neon' },
    // Rain puddles on the neon night streets, a cone-marked works lane, and a
    // neon chevron pad on the Minato straight.
    features: [
      { kind: 'slick', at: 0.25, lateral: -5, length: 9, halfWidth: 4, style: 'puddle' },
      { kind: 'obstacle', at: 0.4, lateral: 10, radius: 1.6, style: 'cone' },
      { kind: 'slick', at: 0.55, lateral: 4, length: 9, halfWidth: 4, style: 'puddle' },
      { kind: 'boost', at: 0.68, lateral: 0, length: 22, halfWidth: 4, style: 'neon' },
    ],
    // M99: a night cab sliding across the scramble, roof sign lit.
    movers: [{ at: 0.47, style: 'keicab', speed: 6.5, dwell: 3 }],
    // M116 SECRET SHORTCUT — the YOKOCHO: a lantern-strung izakaya alley off
    // Shibuya's right kerb. Any pace — pure precision, glowing in the night.
    // (M116 yokocho retired — replaced by the batch-2 aerial cut below.)
    // M117 batch-2 SECRET SHORTCUT — the ABANDONED EXPRESSWAY SPUR. Late
    // Shibuya (13–16%) is the city's ONE clean runway (curvature 0.0046,
    // measured — every other window fails the ≤0.006 bar) ending at a kicker
    // on the player's RIGHT (world side −1 — §6.2: world +1 renders
    // screen-left); commit (~34+) and you loft onto a derelict elevated
    // expressway exactly where the street starts its 18.5 u PLUNGE toward
    // Asakusa — the deck skims the block's crest (2.5–4.8 u over the rising
    // ground, the crest-relief pass in placeShortcuts — this window is WHY
    // that pass exists), then the city falls away beneath it (8–11 u of air),
    // swinging ~106 u out through the Shibuya tower block before chuting
    // down at 37%, where Asakusa begins. Cuts a 44° bend for ≈ +4.6 s at
    // perfect execution; falls 18.4 u end to end (5.5% average — the
    // fastest-draining deck of the four: a dead-stopped ball rolls itself
    // out in 6–42 s, measured). Measured clear: shibuyaTower@0.12 stands on
    // the OPPOSITE side (the spine never comes within 108 u of it);
    // kaminarimon@0.45 is 166 u past the exit; cone@0.4 keeps 66 u off the
    // exit. `look: 'expressway'` is VISUAL only — the deck physics is NYC's
    // highline, byte for byte.
    shortcuts: [{ style: 'highline', look: 'expressway', at: 0.157, exitAt: 0.37, side: -1, bow: 16 }],
    // GS1 — an ink-quiet bay: the ripple calmed hard so the night water reads
    // as still dark glass with neon reflections, never molten bronze quilting.
    // roughness 0.38 (not 0.2) is MEASURED, not taste: at 0.2 the bay mirrors
    // the warm-olive HDRI horizon and 79.8% of grazing-bay pixels read warm
    // (R > B+6); 0.38 drops that to 5.0% while the near-field ripple and cyan
    // glints survive (0.46 starts flattening the water). NOTE `envMapIntensity`
    // is a NO-OP on this material (0 vs 20 = zero pixel delta, measured) —
    // roughness and theme.env.intensity are the only live levers here.
    water: { normalScale: 0.4, roughness: 0.38, ripple: 0.8 },
  },
  {
    // 5 — RIO · bright tropical noon, turquoise bay, lush green hills.
    id: 'rio',
    name: 'Rio de Janeiro',
    country: 'Brazil',
    theme: {
      name: 'Rio de Janeiro',
      background: 0x9fe8e0,
      sky: { top: 0x0fa3d6, horizon: 0xa6ece4 },
      fog: 0xb8eee6,
      sun: 0xfff6dc,
      sunIntensity: 2.9,
      hemiSky: 0xc2e2ea, // pale tropical fill — whites stay white in the shade
      hemiGround: 0x8a7a52, // warm sand bounce
      hemiIntensity: 1.15,
      road: 0x33343c,
      roadLine: 0xffe24a,
      ground: 0x2faa55,
      wall: 0x14d2a0,
      wallStyle: 'wave',
      roadStyle: 'brBeach',
      // Graphics overhaul: bright tropical blue with cumulus over Copacabana.
      env: { hdri: 'env/kloofendal_partly_cloudy.jpg', rotationDeg: 40, backgroundBlur: 0.02 },
      roadTex: { base: 'textures/asphalt_02', repeat: [6, 1.6], normalScale: 0.85, roughness: 0.92 },
      // rio-nits: the Atlantic fills Copacabana's seaward sweep (side +1,
      // 0..~0.32) — where the opening bend curls the ground apron's outer
      // edge across a LOWER stretch's sea it poked +1.0-1.5 above the
      // surface (a green "islet" off the beach, measured at arc ~0.073).
      // Same GS7 sink the Sea Link bay uses: let the apron dive under the
      // declared open water instead of shelving over it.
      apronSink: [{ side: 1, from: 0, to: 0.33 }],
    },
    track: track(0.1, 10, 28),
    difficulty: 'medium',
    seed: 2016,
    lat: -22.91,
    lon: -43.17,
    // Copacabana's wave promenade → under the Lapa arches → the Tijuca
    // rainforest below Christ → a palm boulevard into the Maracanã.
    route: [
      { until: 0.32, kind: 'copacabana', label: 'Copacabana' },
      { until: 0.55, kind: 'lapa', label: 'Lapa' },
      { until: 0.8, kind: 'tijuca', label: 'Tijuca' },
      { until: 1.0, kind: 'maracana', label: 'Maracanã' },
    ],
    landmarks: [
      { kind: 'sugarloaf', at: 0.16, side: 1 }, // across the water
      { kind: 'arcosDaLapa', at: 0.45, side: 0 }, // drive under the arches
      { kind: 'christRedeemer', at: 0.68, side: -1 }, // up on his peak
      { kind: 'maracana', at: 1.0, side: 0 }, // the bowl behind the line
    ],
    // A smooth sand wedge off Copacabana beach — jump the promenade (M47).
    ramp: { at: 0.22, length: 10, halfWidth: 5, height: 2.8, color: 0xe8d0a0, emissive: 0x2a2008, steps: 1, build: 'sand' },
    // Beach sand drifting onto the road, a kiosk crate spill in Lapa, and a
    // green-and-gold carnival pad through Tijuca.
    features: [
      { kind: 'slick', at: 0.12, lateral: 8, length: 10, halfWidth: 5, style: 'sand' },
      { kind: 'obstacle', at: 0.42, lateral: -10, radius: 1.8, style: 'crate' },
      { kind: 'boost', at: 0.62, lateral: 0, length: 20, halfWidth: 4, style: 'drums' },
    ],
    // M99: a runaway GIANT beach ball bounding across Copacabana — sized well
    // past any racer's ball (radius 2.2) and placed clear of the opening pack
    // so it never reads as a game ball (measured at 0.07: it mingled with the
    // grid's balls).
    movers: [{ at: 0.17, style: 'beachball', speed: 5.5, dwell: 2.5, halfLen: 0, radius: 2.2 }],
    // (M117c: Rio's cavern is RETIRED for a from-scratch redo — the owner
    // called the chosen area bad. The 'cavern' style machinery stays for the
    // rebuild; Rio ships without a shortcut until then.)
    // GS1 — Copacabana surf: a white foam line where the turquoise Atlantic
    // laps over the beach sand (the merged sheet tucks under the sand strip).
    water: { shore: 'beach' },
  },
  {
    // 6 — DAKAR · Sahel desert haze, dusty ochre horizon, hard sun, sand.
    id: 'dakar',
    name: 'Dakar',
    country: 'Senegal',
    theme: {
      name: 'Dakar',
      background: 0xe8c88c,
      sky: { top: 0x6f9fc0, horizon: 0xe8c88c },
      fog: 0xe0c79a,
      sun: 0xfff0c0,
      sunIntensity: 1.9, // dust-softened sun — harmattan haze diffuses everything
      hemiSky: 0xe2cfa2,
      hemiGround: 0x8a6a3c,
      hemiIntensity: 1.55,
      road: 0x4a4438,
      roadLine: 0xf2d35e,
      ground: 0xcfa566,
      wall: 0xff9a2c,
      wallStyle: 'render',
      roadStyle: 'sahel',
      // Graphics overhaul: hard Sahel sun through harmattan dust haze; bone-dry
      // matte road.
      env: { hdri: 'env/qwantani_afternoon.jpg', rotationDeg: 45, backgroundBlur: 0.05 },
      roadTex: { base: 'textures/asphalt_02', repeat: [6, 1.6], normalScale: 0.9, roughness: 1.0 },
    },
    track: track(0.1, 10, 26),
    difficulty: 'hard',
    seed: 1979,
    lat: 14.72,
    lon: -17.47,
    // Whitewashed medina → market bustle → open baobab savanna with a DUNE
    // JUMP → the pink lake, finishing under a rally gantry. Dakar Rally soul.
    route: [
      { until: 0.3, kind: 'medina', label: 'Médina' },
      { until: 0.55, kind: 'market', label: 'Sandaga' },
      { until: 0.85, kind: 'savanna', label: 'Savanna' },
      { until: 1.0, kind: 'lacrose', label: 'Lac Rose' },
    ],
    landmarks: [
      { kind: 'grandeMosquee', at: 0.16, side: -1 },
      { kind: 'goreeHouse', at: 0.42, side: 1 },
      { kind: 'renaissanceMonument', at: 0.6, side: -1 }, // on its hill
      { kind: 'giantBaobab', at: 0.76, side: 1 },
      { kind: 'rallyArch', at: 1.0, side: 0 }, // the gantry over the line
    ],
    // A smooth sand dune mid-savanna — the rally jump.
    ramp: { at: 0.7, length: 11, halfWidth: 5.5, height: 3.2, color: 0xd8b070, emissive: 0x2a1c08, steps: 1, build: 'dune' },
    // Rally country: big sand drifts you slide across + fallen rocks. No pads —
    // the dune jump is the reward here.
    features: [
      { kind: 'obstacle', at: 0.35, lateral: 10, radius: 2, style: 'rock' },
      { kind: 'slick', at: 0.45, lateral: -6, length: 14, halfWidth: 6, style: 'sand' },
      { kind: 'slick', at: 0.6, lateral: 5, length: 12, halfWidth: 5, style: 'sand' },
      { kind: 'obstacle', at: 0.82, lateral: -9, radius: 2, style: 'rock' },
    ],
    // M99: a little line of goats ambling across the rally stage — long dwell,
    // slow amble: generous safe windows, but time it or thread behind them.
    movers: [{ at: 0.52, style: 'goats', speed: 4.5, dwell: 4, halfLen: 2.6, radius: 0.9 }],
    // M116 SECRET SHORTCUT — the DUNE CREST: two rally flags mark an off-piste
    // line over the ridge. The fastest gate in the game (min 44 — flat-out in
    // anything, easy money in the upper rides): crest it and fly the desert.
    // (M116 dune retired — Dakar's M117 shortcut arrives in batch 2.)
    // GS1 — Lac Rose keeps its loved pink ripple; the shoreline gains the
    // pale salt-crust rim the real lake wears.
    water: { shore: 'salt' },
  },
  {
    // 7 — LAGOS · warm vibrant tropical afternoon, green + saffron energy.
    id: 'lagos',
    name: 'Lagos',
    country: 'Nigeria',
    theme: {
      name: 'Lagos',
      background: 0xffd089,
      sky: { top: 0x1f8fd0, horizon: 0xffd089 },
      fog: 0xf0c890,
      sun: 0xffe6b0,
      sunIntensity: 2.7,
      hemiSky: 0x9fc8e0,
      hemiGround: 0x4a5a2e,
      hemiIntensity: 1.05,
      road: 0x36373d,
      roadLine: 0xffce3c,
      // lagos-ts: was 0x3f9a4a — a fully saturated emerald. The ground ribbon
      // carries NO texture in this city (theme.groundTex is unset and there is
      // no grass PBR asset), so that tint painted enormous flat sheets that
      // read as neon plastic from any elevated view (QA "plastic tabletop").
      // A sun-bleached khaki-green sits inside the saffron afternoon palette
      // (it is the hemiGround olive, lifted) and stops fighting the water.
      // MEASURED in two steps: 0x6a8a54 still lit bright under sunIntensity
      // 2.7 + the warm IBL, so it went one more step toward dry-season grass.
      ground: 0x7c8552,
      wall: 0x14c878,
      wallStyle: 'chevron',
      roadStyle: 'worn',
      // Graphics overhaul: warm golden-hazy tropical afternoon (saffron horizon).
      env: { hdri: 'env/kloppenheim_06.jpg', rotationDeg: 35, backgroundBlur: 0.04 },
      roadTex: { base: 'textures/asphalt_02', repeat: [6, 1.6], normalScale: 0.85, roughness: 0.9 },
      // lagos-ts (GS7 sink): the lagoon fills BOTH sides of the Third Mainland
      // causeway (route 0.3-0.6) and the seaward side of the Lekki run
      // (0.88-1.0). Where the looping course curls back on itself the ground
      // apron's outer edge (hw+80) sweeps flat across those water passes and
      // shelves OVER them as bright green wedges — measured on the QA seed at
      // up to 4.2 u above the surface (src 0.44 → dest 0.406 side +1) and
      // 7.1 u on the left (src 0.27 → dest 0.182). Declaring the windows lets
      // the apron dive 1.2 u under the foreign water instead. Margins widened
      // a step past the route boundaries so a bend that starts/ends just
      // outside the zone still sinks (the Mumbai/Rio precedent).
      apronSink: [
        { side: -1, from: 0.28, to: 0.62 },
        { side: 1, from: 0.28, to: 0.62 },
        { side: 1, from: 0.86, to: 1 },
      ],
    },
    track: track(0.105, 9, 24),
    difficulty: 'hard',
    seed: 1960,
    lat: 6.52,
    lon: 3.38,
    // Market mainland → the causeway across the lagoon (water both sides,
    // stilt village) → Victoria Island towers → under the Lekki-Ikoyi pylon.
    route: [
      { until: 0.3, kind: 'balogun', label: 'Balogun' },
      { until: 0.6, kind: 'lagoon', label: 'Third Mainland' },
      { until: 0.88, kind: 'victoriaIsland', label: 'Victoria Island' },
      { until: 1.0, kind: 'lekki', label: 'Lekki' },
    ],
    landmarks: [
      { kind: 'lagoonLighthouse', at: 0.45, side: 1 }, // out on the water
      { kind: 'nationalTheatre', at: 0.7, side: -1 },
      { kind: 'cocoaHouse', at: 0.8, side: 1 },
      { kind: 'lekkiIkoyi', at: 1.0, side: 0 }, // finish under the cables
    ],
    // Balogun market crates spilling into the road, a BRT pad down the
    // causeway, lagoon splash near Victoria Island.
    features: [
      { kind: 'obstacle', at: 0.15, lateral: 9, radius: 1.8, style: 'crate' },
      { kind: 'obstacle', at: 0.25, lateral: -10, radius: 1.8, style: 'crate' },
      { kind: 'boost', at: 0.45, lateral: 0, length: 22, halfWidth: 4, style: 'brt' },
      { kind: 'slick', at: 0.7, lateral: -4, length: 9, halfWidth: 4, style: 'puddle' },
    ],
    // M99: a timber jetty wedge off the lagoon causeway — launch over the water.
    // M117c: the jetty ramp ENLARGED (owner: "make the existing ramp bigger")
    // — it is now the launcher for the sky-path shortcut. Slope stays ~1/3 so
    // AI hops off it barely change; the bigger footprint is the invitation.
    // M117d (owner: "I'd like the Lagos ramp to be better designed with
    // texture"): the jetty BUILD — a planked pier wedge on timber piles with
    // stringer beams, cross-braces and mooring posts. Physics untouched.
    ramp: {
      at: 0.56,
      length: 13,
      halfWidth: 6.5,
      height: 4.6,
      color: 0x9a6b3a,
      emissive: 0x2a1a08,
      steps: 1,
      build: 'jetty',
    },
    // M99: a danfo lurching across Balogun (yellow, of course).
    movers: [{ at: 0.33, style: 'danfo', speed: 6, dwell: 3, halfLen: 2.2, radius: 1.35 }],
    // M116 SECRET SHORTCUT — the STILT-VILLAGE BOARDWALK: a plank causeway
    // across the lagoon inlet. Carry real pace (min 26) to make the first
    // plank gap, then rattle the boards over the water.
    // M117c SECRET SHORTCUT — the LAGOON SKY-PATH (owner spec: "make the
    // existing ramp bigger and have it lead to a sky-path of sorts over the
    // water that rides to the left of the big buildings on the left side").
    // The catch deck hangs 20 u past the enlarged ramp's lip on its real
    // flight line — carry ~32+ u/s off the ramp with the ball on your nose
    // and you land the timber sky-path, which swings LEFT out over the
    // lagoon past the waterfront blocks and chutes back in at ~67%. Road
    // traffic (and every bot hop) passes clean underneath the deck.
    shortcuts: [{ style: 'skypath', at: 0.56, exitAt: 0.672, side: 1, bow: 24 }],
    // GS1 — the lagoon: merged shore-conforming sheets with a warm MUD bank
    // where the terraced ground steps used to poke through as green wedges.
    water: { shore: 'mud' },
  },
  {
    // 8 — MUMBAI · golden monsoon haze, saffron rails, dusty green.
    id: 'mumbai',
    name: 'Mumbai',
    country: 'India',
    theme: {
      name: 'Mumbai',
      background: 0xe8c068,
      sky: { top: 0x9a9fb0, horizon: 0xe8c068 },
      fog: 0xd8b878,
      sun: 0xffe0a0,
      sunIntensity: 2.3,
      hemiSky: 0xd8cdb0,
      hemiGround: 0x7a6648, // warm haze bounce — basalt stays readable in shade
      hemiIntensity: 1.35,
      road: 0x3a3940,
      roadLine: 0xf2c84a,
      ground: 0x6a7a40,
      wall: 0xff8a3c,
      wallStyle: 'seawall',
      roadStyle: 'inMix',
      // Graphics overhaul: soft golden monsoon haze over the Queen's Necklace.
      env: { hdri: 'env/qwantani_late_afternoon.jpg', rotationDeg: 40, backgroundBlur: 0.05 },
      roadTex: { base: 'textures/asphalt_02', repeat: [6, 1.6], normalScale: 0.85, roughness: 0.88 },
      // GS7: the Arabian Sea fills the loops of Marine Drive + Haji Ali
      // (side +1, ~0.3-0.8) — let the ground apron sink under it where the
      // course curls back on itself (the Sea Link bay's floating-slab fix).
      apronSink: [{ side: 1, from: 0.28, to: 0.82 }],
    },
    track: track(0.11, 9, 24),
    difficulty: 'hard',
    seed: 1995,
    lat: 19.08,
    lon: 72.88,
    // Gothic Fort → the Queen's Necklace along the sea → the dargah causeway
    // → Colaba, finishing THROUGH the Gateway of India.
    route: [
      { until: 0.3, kind: 'fort', label: 'Fort' },
      { until: 0.6, kind: 'marinedrive', label: 'Marine Drive' },
      { until: 0.8, kind: 'hajiali', label: 'Haji Ali' },
      { until: 1.0, kind: 'colaba', label: 'Colaba' },
    ],
    landmarks: [
      { kind: 'cst', at: 0.15, side: -1 },
      { kind: 'seaLink', at: 0.45, side: 1 }, // far out on the sea
      { kind: 'hajiAliDargah', at: 0.7, side: 1 }, // offshore on its causeway
      { kind: 'tajHotel', at: 0.93, side: -1 },
      { kind: 'gatewayOfIndia', at: 1.0, side: 0 }, // finish through the arch
    ],
    // A basalt sea-wall wedge on Marine Drive — launch over the Queen's
    // Necklace sweep (M47).
    ramp: { at: 0.35, length: 9, halfWidth: 5, height: 3, color: 0x4a4640, emissive: 0xff8a3c, steps: 3, build: 'basalt' },
    // Monsoon season: puddles everywhere + a barricaded works lane.
    features: [
      { kind: 'slick', at: 0.2, lateral: 5, length: 9, halfWidth: 4, style: 'puddle' },
      { kind: 'slick', at: 0.55, lateral: -6, length: 9, halfWidth: 4, style: 'puddle' },
      { kind: 'obstacle', at: 0.65, lateral: 10, radius: 1.8, style: 'barrier' },
      { kind: 'slick', at: 0.75, lateral: 2, length: 9, halfWidth: 4, style: 'puddle' },
    ],
    // M99: auto-rickshaws darting across — Mumbai traffic never stops.
    movers: [
      { at: 0.45, style: 'rickshaw', speed: 6.5, dwell: 2.8, halfLen: 1.3, radius: 1 },
      { at: 0.6, style: 'rickshaw', speed: 6.5, dwell: 3, halfLen: 1.3, radius: 1, phase: 8 },
    ],
    // M116 SECRET SHORTCUT — the MONSOON DRAIN: a storm culvert under Fort's
    // left kerb. The mouth is LOW — duck in under 26 or you skip right over
    // the lip; inside, a concrete bore runs the water out to Marine Drive.
    // (exit 0.315, not deeper into Marine Drive — at 0.33 the outfall surfaced
    // inside the bay's water sheet where the road dips below its plane, ball
    // half-submerged; measured on the tour seed.)
    // (M116 drain retired — Mumbai's M117 shortcut arrives in batch 3.)
  },
  {
    // 9 — PATAGONIA · cold blue twilight, icy peaks, glacial cyan. Twisty finale.
    id: 'patagonia',
    name: 'Patagonia',
    country: 'Argentina',
    theme: {
      name: 'Patagonia',
      background: 0xa9c7d9,
      sky: { top: 0x16314f, horizon: 0xafccdd },
      fog: 0xb9d2de,
      sun: 0xcfe0ee,
      sunIntensity: 1.7,
      hemiSky: 0xaecadd,
      hemiGround: 0x3a4a52,
      hemiIntensity: 1.05,
      road: 0x33373f,
      roadLine: 0xdff0ff,
      ground: 0x6f8a7a,
      wall: 0x57e0ff,
      wallStyle: 'timber',
      roadStyle: 'gravel',
      // Graphics overhaul: cold muted blue-grey twilight over the ice; a faint
      // cold sheen on the road.
      env: { hdri: 'env/qwantani_dusk_1.jpg', rotationDeg: 30, backgroundBlur: 0.04 },
      roadTex: { base: 'textures/asphalt_02', repeat: [6, 1.6], normalScale: 0.85, roughness: 0.72 },
      // shared-2a (GS7 window, the Mumbai/Lagos precedent): the iceberg lake
      // and the glacier's meltwater fill side +1 continuously from the
      // Lago Glaciar zone to the line (route 0.5 → 1.0). Where the loop curls
      // back, a higher pass's apron edge shelved OVER the lake as a flat
      // olive sliver (measured src ~0.69 → dest ~0.73, +1.8 u above the
      // surface). The GS8 land bank alone cannot clear this one: it holds the
      // verge out to hw+42, so the sheet still crosses the water plane on its
      // way down. A declared window dives from the rail instead. Margins a
      // step past the route boundary, as Lagos and Rio do.
      apronSink: [{ side: 1, from: 0.48, to: 1 }],
    },
    track: track(0.115, 8, 22),
    difficulty: 'legendary',
    seed: 1520,
    lat: -50.34,
    lon: -72.27,
    // Through the estancia gate → crimson lenga forest → the iceberg lake →
    // snowfields under the ice wall, finishing beneath Fitz Roy. The finale.
    route: [
      { until: 0.25, kind: 'elchalten', label: 'El Chaltén' },
      { until: 0.5, kind: 'lenga', label: 'Lenga Forest' },
      { until: 0.78, kind: 'glaciallake', label: 'Lago Glaciar' },
      { until: 1.0, kind: 'glacier', label: 'The Glacier' },
    ],
    landmarks: [
      { kind: 'estanciaGate', at: 0.04, side: 0 }, // ride out under the ranch gate
      { kind: 'glacierWall', at: 0.86, side: 1 }, // the blue wall beside the road
      { kind: 'fitzRoy', at: 1.0, side: 0 }, // the massif behind the line
    ],
    // The finale is hazards-only: moraine rockfall + ICE near the lake and the
    // glacier. Ice patches sit on gentler stretches (legendary AI corners hard).
    features: [
      { kind: 'obstacle', at: 0.3, lateral: 9, radius: 2, style: 'rock' },
      { kind: 'slick', at: 0.55, lateral: -4, length: 10, halfWidth: 5, style: 'ice' },
      { kind: 'obstacle', at: 0.7, lateral: -10, radius: 2, style: 'rock' },
      { kind: 'slick', at: 0.85, lateral: 3, length: 12, halfWidth: 5, style: 'ice' },
    ],
    // M99: a wind-packed snow bank by the lake — the finale finally gets a jump.
    ramp: { at: 0.63, length: 10, halfWidth: 5, height: 2.8, color: 0xdfe8f2, emissive: 0x18303c, steps: 1, build: 'snow' },
    // M99: guanacos crossing near the lake — unhurried, long safe windows.
    movers: [{ at: 0.4, style: 'guanaco', speed: 5, dwell: 4, halfLen: 1.5, radius: 1 }],
    // M116 SECRET SHORTCUT — the CREVASSE: a crack in the glacier wall itself.
    // Ice inside means a CAREFUL entry (max 24) — slip into the blue bore and
    // it carries you beneath the ice to the snowfields. The finale's secret.
    // (M116 crevasse retired — Patagonia's M117 shortcut arrives in batch 3.)
    // GS1 (owner bar: surfaces sit IN the twilight) — the swimming-pool cyan
    // desaturated toward milky glacial turquoise, the busy bubble-wrap ripple
    // broadened into wind lanes, reflection + gloss pulled way down. The
    // `from` hexes must match the zone emitters (palettes GLACIAL_LAKE + the
    // glacier zone's meltwater literal) — pinned by tests/world/water.test.ts.
    water: {
      shore: 'glacial',
      ripple: 0.55,
      normalScale: 0.42,
      roughness: 0.34,
      envMapIntensity: 0.85,
      recolor: [
        { from: 0x4ab8c8, to: 0x7fb0b4 }, // GLACIAL_LAKE → milky turquoise
        { from: 0x6ac8d8, to: 0x93bfc2 }, // glacier meltwater → pale milk
      ],
    },
  },
];

/** Look up a city by id (falls back to the first city). */
export function cityById(id: string): City {
  return CITIES.find((c) => c.id === id) ?? CITIES[0];
}

/** M87 — every city id in a fresh random order (Fisher–Yates): the offline
 *  tour roll. `rand` is injectable for tests. */
export function shuffledCityIds(rand: () => number = Math.random): string[] {
  const ids = CITIES.map((c) => c.id);
  for (let i = ids.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [ids[i], ids[j]] = [ids[j], ids[i]];
  }
  return ids;
}

/** M87 — build a K-stop tour route: the caller's ordered PICKS race first
 *  (unknown/duplicate ids dropped, extras beyond K cut), then the remaining
 *  stops draw randomly from the other cities. No picks = a fully random
 *  route — the online "Start a Tour" roll and the friend-room RANDOM mode.
 *  Pure + shared with the Cloudflare room (picks come off a user-controlled
 *  URL, so this is also the sanitizer). */
export function buildTourRoute(
  len: number,
  picks: readonly string[],
  rand: () => number = Math.random,
): City[] {
  const n = Math.min(Math.max(2, Math.round(len)), CITIES.length);
  const route: City[] = [];
  const taken = new Set<string>();
  for (const id of picks) {
    if (route.length >= n) break;
    const city = CITIES.find((c) => c.id === id);
    if (city && !taken.has(city.id)) {
      taken.add(city.id);
      route.push(city);
    }
  }
  const rest = CITIES.filter((c) => !taken.has(c.id));
  for (let i = rest.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [rest[i], rest[j]] = [rest[j], rest[i]];
  }
  while (route.length < n && rest.length > 0) route.push(rest.pop() as City);
  return route;
}
